import { useState } from 'react';
import { useSnapshot } from 'valtio';

import postPurchaseOrgCreditPacks from '@cartwhl/common/api/postPurchaseOrgCreditPacks';
import * as noticesStore from '@cartwhl/common/stores/noticesStore';
import type { PurchaseCreditsModalVariant } from '@cartwhl/common/stores/updatedProductStore';
import * as updatedProductStore from '@cartwhl/common/stores/updatedProductStore';
import * as userStore from '@cartwhl/common/stores/userStore';
import handleGenericErrors from '@cartwhl/common/utils/handleGenericErrors';
import {
  formatOrgCreditsForDisplay,
  isOrgCreditsWorkspace,
} from '@cartwhl/common/utils/organizationWorkspace';
import Text from '@cartwhl/ui/base/Text';
import MinusIcon from '@cartwhl/ui/icons/MinusIcon';
import PlusIcon from '@cartwhl/ui/icons/PlusIcon';
import Button from '@cartwhl/ui/input/Button';
import Modal from '@cartwhl/ui/molecules/Modal';

export type PurchaseCreditsModalPayload = {
  variant: PurchaseCreditsModalVariant;
  creditsRemaining: number;
};

const MAX_PACKS = 25;

export default function PurchaseCreditsModal({
  close,
  payload,
}: {
  close: () => void;
  payload: PurchaseCreditsModalPayload;
}) {
  const { organizationInfo } = useSnapshot(userStore.state);
  const [quantity, setQuantity] = useState(1);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const orgCreditsWorkspace = isOrgCreditsWorkspace(organizationInfo);
  const topUpUsd = organizationInfo?.topUpAmountUSD || 25;
  const costPerCredit = organizationInfo?.costPerCredit || 0.05;
  const creditsPerPack =
    topUpUsd > 0 && costPerCredit > 0
      ? Math.round(topUpUsd / costPerCredit)
      : 500;
  const totalCredits = creditsPerPack * quantity;
  const totalUsd = topUpUsd * quantity;

  const title =
    payload.variant === 'out_of_credits'
      ? "You're out of credits"
      : 'Running low on credits';

  const leadSentence =
    payload.variant === 'out_of_credits'
      ? "You're out of credits."
      : `You have ${formatOrgCreditsForDisplay(payload.creditsRemaining)} credits remaining.`;

  const canPurchase = orgCreditsWorkspace && creditsPerPack > 0 && topUpUsd > 0;

  function handleOpenChange(open: boolean) {
    if (!open) {
      close();
    }
  }

  function handleSnoozeTwoHours() {
    const id = organizationInfo?.organizationID?.trim();
    if (id) {
      updatedProductStore.snoozePurchaseCreditsModalForTwoHours(id);
    } else {
      updatedProductStore.closePurchaseCreditsModal();
    }
  }

  async function handleAddCredits() {
    if (!canPurchase || checkoutLoading) return;
    setCheckoutLoading(true);
    const { data, error } = await postPurchaseOrgCreditPacks(
      Math.round(totalUsd * 100) / 100,
    );
    setCheckoutLoading(false);
    if (error) {
      await handleGenericErrors(error);
      return;
    }
    if (data?.paymentIntentId) {
      await userStore.refetchUserInfo();
      noticesStore.success(
        'Payment submitted. Your credit balance should update in a minute.',
      );
      updatedProductStore.closePurchaseCreditsModal();
    }
  }

  return (
    <Modal
      title={title}
      maxWidth={684}
      open={true}
      onOpenChange={handleOpenChange}
    >
      <Text as="p" variant="studio/body/md">
        {leadSentence}{' '}
        {canPurchase ? (
          <>
            Add {formatOrgCreditsForDisplay(totalCredits)} credits for $
            {totalUsd % 1 === 0 ? totalUsd.toFixed(0) : totalUsd.toFixed(2)} to
            keep working without interruption. Your organization will be
            automatically charged using the payment method on file.
          </>
        ) : (
          <>
            Credit purchases are not available for your current plan or billing
            setup.
          </>
        )}
      </Text>
      <div className="bg-gray-15 dark:bg-gray-75 mt-6 flex items-center justify-between rounded-lg py-2">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 pl-4">
            <Button
              className="dark:bg-gray-90 h-8 w-8 bg-white"
              variant="secondary"
              icon={<MinusIcon />}
              size="sm"
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              disabled={!canPurchase || quantity <= 1}
            ></Button>
            <div className="flex min-w-48 items-center justify-center">
              <Text as="span" variant="studio/display/2xl">
                {canPurchase ? formatOrgCreditsForDisplay(totalCredits) : '—'}
              </Text>
            </div>
            <Button
              className="dark:bg-gray-90 h-8 w-8 bg-white"
              variant="secondary"
              size="sm"
              icon={<PlusIcon />}
              disabled={!canPurchase || quantity >= MAX_PACKS}
              onClick={() => setQuantity(quantity + 1)}
            ></Button>
            <div className="pt-8">
              <Text
                as="span"
                variant="studio/body/bold/md"
                className="font-display"
              >
                Credits
              </Text>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 pr-4">
          <Text as="span" variant="studio/body/md">
            Total:
          </Text>
          <Text as="span" variant="studio/body/bold/md">
            {canPurchase
              ? `$${totalUsd % 1 === 0 ? totalUsd.toFixed(0) : totalUsd.toFixed(2)}`
              : '—'}
          </Text>
        </div>
      </div>
      <div className="mt-6 flex flex-row flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={close}>
            Not now
          </Button>
          <Button variant="secondary" onClick={handleSnoozeTwoHours}>
            Snooze for 2 hours
          </Button>
        </div>
        <Button
          className="ml-auto"
          variant="primary"
          onClick={handleAddCredits}
          disabled={!canPurchase || checkoutLoading}
        >
          {checkoutLoading ? 'Loading…' : 'Add Credits'}
        </Button>
      </div>
    </Modal>
  );
}
